"use client";

import React from "react";
import { MessageCircle, Phone, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "Why choose a mobile locksmith over the dealership?",
  answer: "Choosing AngliaKeys means you don't need to tow your vehicle to a dealership. We come directly to your location in Norfolk and East Anglia, often providing a faster service at a significantly more competitive price. Our specialized equipment allows us to cut and program keys on-site, saving you both time and money."
}, {
  question: "How does your pricing work?",
  answer: "We believe in complete transparency. Unlike some services that add hidden fees, we provide clear, upfront quotes before any work begins. Our pricing is competitive and reflects the high quality of our mechanical precision and digital expertise."
}, {
  question: "I've lost all my keys. Can you still help?",
  answer: "Absolutely. We specialize in 'all keys lost' scenarios. Using advanced decoding tools, we can generate a new key blade and program a new transponder chip to your vehicle's immobiliser system, getting you back on the road without needing a single original key."
}, {
  question: "How quickly can you reach me?",
  answer: "We pride ourselves on rapid response times for emergency lockouts. Being locally based in Norfolk, we can typically reach most locations within the hour, traffic permitting. Our 24/7 availability ensures you're never left stranded for long."
}, {
  question: "Do you cover my specific vehicle make and model?",
  answer: "We have extensive automotive knowledge covering a vast range of manufacturers, from common family cars to high-end vehicles. Our technicians are equipped with the latest diagnostic software to handle key programming for almost any make and model on the UK roads."
}];
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="common-questions" className="bg-background relative overflow-hidden py-24">
      {}
      <div className="pointer-events-none absolute inset-0 flex justify-center overflow-hidden">
        <div className="bg-primary/5 absolute top-0 h-[500px] w-[500px] rounded-full blur-3xl" />
        <div className="bg-accent/5 absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`container relative mx-auto px-4 transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
        <div className="mb-12 text-center">
          <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Common Questions
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            Clear answers about our automotive locksmith services, pricing, and
            emergency assistance.
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="bg-card/50 border-border/50 hover:border-primary/30 group overflow-hidden rounded-lg border transition-all duration-300">
                <AccordionTrigger className="text-foreground hover:text-primary hover:no-underline relative px-6 py-5 text-left text-base font-medium transition-colors [&[data-state=open]]:text-primary">
                  <span className="bg-primary absolute left-0 top-0 bottom-0 h-full w-1 scale-y-0 transition-transform duration-300 ease-out group-hover:scale-y-50 group-data-[state=open]:scale-y-100 group-data-[state=open]:shadow-[0_0_10px_var(--color-primary)]" />
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground px-6 pb-5 text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className="mt-16 flex flex-col items-center justify-center gap-6 text-center">
          <p className="text-foreground text-lg font-medium">
            Still have questions? We're here to help.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Button asChild size="lg" className="group">
              <Link to="https://wa.me/447700123456" newTab>
                <MessageCircle className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
                Chat with us
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="group">
              <Link to="tel:07700123456">
                <Phone className="mr-2 h-5 w-5 transition-transform group-hover:rotate-12" />
                Call 07700 123456
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}